import type { BotConversationState, BotRunawayCaps } from '../../types/leadBot.js';

// SMS Lead Bot — runaway protection. PURE.
//
// ⚠️ These are not tuning knobs. They are the ceiling on how badly a loop can go
// wrong while nobody is watching: an auto-responder ping-pong burns money on
// every turn and looks deranged to whoever eventually reads the transcript, and
// a per-conversation cap is the difference between "the bot was a bit chatty"
// and "we texted a member of the public forty times".
//
// They are CONFIGURABLE PER BOT (BotConfig.caps) because a busy dealership and a
// quiet one need different numbers — but ⚠️ configurable within bounds, never
// off. See CAP_BOUNDS below for why zero is refused rather than treated as
// unlimited.
//
// The caps are checked at TWO points, deliberately:
//   - before DRAFTING, so a capped conversation costs no Claude call;
//   - before SENDING, so an approval on a stale queue entry cannot slip past a
//     cap that came into force after the draft was written.
// Checking only at draft time would leave the second door open.

export type RunawayReason =
  | 'conversation_cap'
  | 'hourly_cap'
  | 'automated_counterparty'
  | 'conversation_stopped'
  | 'conversation_closed';

export type RunawayVerdict =
  | { blocked: false }
  | { blocked: true; reason: RunawayReason; detail: string };

export const DEFAULT_RUNAWAY_CAPS: BotRunawayCaps = {
  maxMessagesPerConversation: 12,
  maxOutboundPerHour: 30,
};

/**
 * ⚠️⚠️ THERE IS NO "UNLIMITED", AND ZERO IS NOT A WAY TO ASK FOR ONE.
 *
 * Same reasoning as the allowlist: the cap exists for an auto-responder loop at
 * 3am, not for a polite tester, and an autonomous bot in a loop overnight is
 * real money and a flood of SMS to a real phone. A high finite number is
 * effectively unlimited for anyone with a legitimate need AND keeps the circuit
 * breaker; an off switch removes the breaker entirely, and the difference only
 * shows up on the night it matters.
 *
 * So the minimum is 1 and blank/zero is REFUSED with a message, rather than
 * quietly meaning "no ceiling". The maxima are the ceiling on what someone can
 * type into a form — deliberately generous, so raising a cap is a normal
 * adjustment rather than a thing people work around.
 */
export const CAP_BOUNDS = {
  maxMessagesPerConversation: { min: 1, max: 50, label: 'Replies per conversation' },
  maxOutboundPerHour: { min: 1, max: 500, label: 'Replies per hour' },
} as const satisfies Record<keyof BotRunawayCaps, { min: number; max: number; label: string }>;

export type CapField = keyof typeof CAP_BOUNDS;

/**
 * Validate one cap. Returns the message to show, or null when it is fine.
 *
 * ⚠️ ONE implementation, used by the zod schema AND the settings form, so the
 * two cannot disagree about what is acceptable — a form that accepts a value
 * the API then rejects is how people learn to distrust a save button.
 */
export function capError(value: unknown, field: CapField): string | null {
  const bounds = CAP_BOUNDS[field];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return `${bounds.label} needs a number between ${bounds.min} and ${bounds.max}.`;
  }
  if (!Number.isInteger(value)) return `${bounds.label} must be a whole number.`;
  if (value < bounds.min) {
    // ⚠️ The message says WHY rather than just restating the rule, because the
    // person hitting it is usually trying to turn the cap off.
    return (
      `${bounds.label} must be at least ${bounds.min}. There is no unlimited setting — ` +
      `the cap is what stops a reply loop running all night. Set it high instead.`
    );
  }
  if (value > bounds.max) return `${bounds.label} cannot be more than ${bounds.max}.`;
  return null;
}

/**
 * ⚠️ THE LAST LINE OF DEFENCE, and the reason `assessRunaway` calls it rather
 * than trusting what it was handed. A stored 0 (a legacy doc, a hand-edit, a
 * missing bot config) must never resolve to "no ceiling" — it resolves to the
 * DEFAULT. Falling back to the default rather than blocking outright is
 * deliberate too: an unreadable cap is a config problem, and a bot that
 * silently answers nobody is harder to notice than one running at 12 and 30.
 */
export function resolveRunawayCaps(
  stored: Partial<BotRunawayCaps> | null | undefined,
): BotRunawayCaps {
  const pick = (field: CapField): number => {
    const value = stored?.[field];
    return capError(value, field) === null ? (value as number) : DEFAULT_RUNAWAY_CAPS[field];
  };
  return {
    maxMessagesPerConversation: pick('maxMessagesPerConversation'),
    maxOutboundPerHour: pick('maxOutboundPerHour'),
  };
}

/**
 * Phrases that mean the other end is a machine. ⚠️ Detecting this matters more
 * than it looks: two bots replying to each other never terminates on its own,
 * and every turn is a paid Claude call plus a paid SMS.
 */
const AUTOMATED_MARKERS: RegExp[] = [
  /\bauto[\s-]?(reply|response|responder|generated)\b/i,
  /\bautomated\s+(message|response|reply|system)\b/i,
  /\bout\s+of\s+(the\s+)?office\b/i,
  /\bdo\s*not\s*reply\b/i,
  /\bdon'?t\s+reply\s+to\s+this\b/i,
  /\bno[\s-]?reply@/i,
  /\bunattended\s+(mailbox|inbox|number)\b/i,
  /\bthis\s+(mailbox|number)\s+is\s+not\s+monitored\b/i,
  /\bmessage\s+sent\s+from\s+an\s+automated\b/i,
];

export function looksAutomated(text: string): boolean {
  if (typeof text !== 'string' || text.trim() === '') return false;
  return AUTOMATED_MARKERS.some((re) => re.test(text));
}

export interface RunawayInput {
  state: BotConversationState;
  /** Outbound messages actually SENT in this conversation. */
  outboundSentCount: number;
  /** ISO timestamps of outbound sends across the bot, any conversation. */
  recentOutboundIso: readonly string[];
  /** ⚠️ Coerced through resolveRunawayCaps — a zero here is not an off switch. */
  caps: Partial<BotRunawayCaps> | null | undefined;
  /** The latest inbound text, for the automated-counterparty check. */
  latestInboundText: string | null;
  nowMs: number;
}

const HOUR_MS = 60 * 60 * 1000;

export function assessRunaway(input: RunawayInput): RunawayVerdict {
  if (input.state === 'stopped') {
    return {
      blocked: true,
      reason: 'conversation_stopped',
      detail: 'The lead sent STOP — nothing more may be sent to this number.',
    };
  }
  if (input.state === 'closed') {
    return {
      blocked: true,
      reason: 'conversation_closed',
      detail: 'This conversation is closed.',
    };
  }

  // ⚠️ COERCED, NOT TRUSTED — see resolveRunawayCaps. A caller handing over a
  // zero (or a missing bot config) gets the defaults, never an open throttle.
  const caps = resolveRunawayCaps(input.caps);

  const perConversation = caps.maxMessagesPerConversation;
  if (input.outboundSentCount >= perConversation) {
    return {
      blocked: true,
      reason: 'conversation_cap',
      detail:
        `This conversation has already had ${input.outboundSentCount} replies ` +
        `(cap ${perConversation}). Hand it to a person.`,
    };
  }

  const perHour = caps.maxOutboundPerHour;
  const since = input.nowMs - HOUR_MS;
  const recent = input.recentOutboundIso.filter((iso) => {
    const ms = Date.parse(iso);
    // ⚠️ Fails CLOSED on an unparseable timestamp — an unreadable date counts
    // toward the cap rather than being quietly discounted. The cap exists for
    // the case where something has gone wrong, which is exactly when a
    // timestamp is most likely to be odd.
    return Number.isNaN(ms) || ms >= since;
  }).length;
  if (recent >= perHour) {
    return {
      blocked: true,
      reason: 'hourly_cap',
      detail: `${recent} messages sent in the last hour (cap ${perHour}).`,
    };
  }

  if (input.latestInboundText && looksAutomated(input.latestInboundText)) {
    return {
      blocked: true,
      reason: 'automated_counterparty',
      detail:
        'The last inbound message looks like an auto-responder — stopping rather ' +
        'than replying to a machine.',
    };
  }

  return { blocked: false };
}
